import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
  alpha,
  Box,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';

import { type ActivityUsageGranularity, useActivityUsage } from '../../api/index.js';
import { ActivityAccuracyPanel } from '../../components/Activity/index.js';
import SummaryStat from '../../components/Activity/SummaryStat.js';
import { sumUsageByPatientMonth, usageMonthKey } from '../../components/Activity/usageMonthlyTotals.js';

function formatThousands(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toString();
}

const AiUsageDashboard = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [granularity, setGranularity] = useState<ActivityUsageGranularity>('month');
  const [fromDate, setFromDate] = useState<string>(
    DateTime.now().startOf('month').toISODate() ?? '',
  );
  const [toDate, setToDate] = useState<string>(DateTime.now().toISODate() ?? '');

  const from = useMemo(
    () => DateTime.fromISO(fromDate).startOf('day').toISO() ?? '',
    [fromDate],
  );
  const to = useMemo(() => DateTime.fromISO(toDate).endOf('day').toISO() ?? '', [toDate]);

  const usage = useActivityUsage(
    { from, to, granularity },
    { enabled: Boolean(from && to) && activeTab === 0, keepPreviousData: true },
  );

  const items = useMemo(() => usage.data?.items ?? [], [usage.data]);
  const monthlyTargetUsd = usage.data?.monthlyTargetUsd ?? null;
  const isMonthly = granularity === 'month';
  const monthTotals = useMemo(() => sumUsageByPatientMonth(items), [items]);
  const isOverTarget = (key: string) =>
    isMonthly && monthlyTargetUsd !== null && (monthTotals.get(key) ?? 0) > monthlyTargetUsd;

  const totals = useMemo(
    () =>
      items.reduce(
        (acc, it) => ({
          events: acc.events + it.events,
          inputTokens: acc.inputTokens + it.inputTokens,
          outputTokens: acc.outputTokens + it.outputTokens,
          costUsd: acc.costUsd + it.costUsd,
        }),
        { events: 0, inputTokens: 0, outputTokens: 0, costUsd: 0 },
      ),
    [items],
  );

  return (
    <Box sx={{ m: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <AutoAwesomeIcon color="primary" />
        <Typography variant="h4" component="h1">
          AI usage
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Estimated Bedrock spend and review agreement across all patients. Costs are approximate
        (current list pricing) — refer to the AWS Budget for billing-grade numbers.
      </Typography>

      <Paper variant="outlined" sx={{ px: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
        >
          <Tabs value={activeTab} onChange={(_, value: number) => setActiveTab(value)}>
            <Tab label="Usage" />
            <Tab label="Accuracy" />
          </Tabs>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1 }}>
            <TextField
              type="date"
              label="From"
              size="small"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              type="date"
              label="To"
              size="small"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <ToggleButtonGroup
              size="small"
              exclusive
              value={granularity}
              onChange={(_, v: ActivityUsageGranularity | null) => v && setGranularity(v)}
            >
              <ToggleButton value="day">Daily</ToggleButton>
              <ToggleButton value="month">Monthly</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Stack>
      </Paper>

      {activeTab === 1 ? (
        <ActivityAccuracyPanel from={from} to={to} granularity={granularity} />
      ) : (
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" spacing={4}>
              <SummaryStat label="Total cost (est.)" value={`$${totals.costUsd.toFixed(2)}`} emphasised />
              <SummaryStat label="Events" value={totals.events.toLocaleString()} />
              <SummaryStat label="Input tokens" value={formatThousands(totals.inputTokens)} />
              <SummaryStat label="Output tokens" value={formatThousands(totals.outputTokens)} />
            </Stack>
          </Paper>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{granularity === 'month' ? 'Month' : 'Day'}</TableCell>
                  <TableCell>Patient</TableCell>
                  <TableCell>Model</TableCell>
                  <TableCell align="right">Events</TableCell>
                  <TableCell align="right">Input tokens</TableCell>
                  <TableCell align="right">Output tokens</TableCell>
                  <TableCell align="right">Avg latency</TableCell>
                  <TableCell align="right">Cost (est.)</TableCell>
                  {isMonthly ? <TableCell align="right">Patient month vs target</TableCell> : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((it) => (
                  <TableRow
                    key={`${it.patientId}::${it.day}::${it.modelId}`}
                    hover
                    sx={isOverTarget(usageMonthKey(it))
                      ? { bgcolor: (theme) => alpha(theme.palette.error.main, 0.08) }
                      : undefined}
                  >
                    <TableCell>{it.day}</TableCell>
                    <TableCell>{it.patientName ?? it.patientId}</TableCell>
                    <TableCell>{it.modelId}</TableCell>
                    <TableCell align="right">{it.events.toLocaleString()}</TableCell>
                    <TableCell align="right">{formatThousands(it.inputTokens)}</TableCell>
                    <TableCell align="right">{formatThousands(it.outputTokens)}</TableCell>
                    <TableCell align="right">{it.avgLatencyMs > 0 ? `${it.avgLatencyMs} ms` : '—'}</TableCell>
                    <TableCell align="right">${it.costUsd.toFixed(2)}</TableCell>
                    {isMonthly ? (
                      <TableCell align="right">
                        ${(monthTotals.get(usageMonthKey(it)) ?? 0).toFixed(2)}
                        {monthlyTargetUsd !== null ? ` / $${monthlyTargetUsd.toFixed(0)}` : ''}
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
                {!usage.isLoading && items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isMonthly ? 9 : 8} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                      No AI usage in this period.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
          {monthlyTargetUsd !== null ? (
            <Typography variant="body2" color="text.secondary">
              {isMonthly
                ? `Rows whose patient is over the $${monthlyTargetUsd.toFixed(0)} monthly target are highlighted.`
                : `Switch to Monthly to compare each patient against the $${monthlyTargetUsd.toFixed(0)} target; ` +
                  'daily rows are not compared because captures only run inside check-in windows.'}
            </Typography>
          ) : null}
        </Stack>
      )}
    </Box>
  );
};

export default AiUsageDashboard;
